import React, { useState, useEffect } from 'react';
import { Card, AnalyticalCardHeader, NumericSideIndicator } from '@ui5/webcomponents-react';
import { LineChart } from '@ui5/webcomponents-react-charts';

const ExpenseAmountDistributionCard = ({ expenseData }) => {
  const [exchangeRates, setExchangeRates] = useState(null);

  // Fetch exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/sar.json');
        if (!response.ok) {
          throw new Error('Failed to fetch exchange rates');
        }
        const data = await response.json();
        setExchangeRates(data.sar);
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
      }
    };

    fetchExchangeRates();
  }, []);

  // Function to convert amount to SAR
  const convertToSAR = (amount, currency) => {
    if (!exchangeRates || !currency || currency === 'SAR' || !exchangeRates[currency.toLowerCase()]) return amount;
    return amount / exchangeRates[currency.toLowerCase()];
  };

// Function to format data for the line chart
const formatDataForChart = () => {
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const data = new Array(12).fill(0);

  expenseData.forEach((expense) => {
    try {
      const amount = expense.total_base_amount;
      const currency = expense.base_currency;
      const convertedAmount = convertToSAR(amount, currency);
      const [month, year] = expense.month_year.split('/').map(Number);
      const scanDate = new Date(year, month - 1); // Month is 0-based in JavaScript Date
      const monthIndex = scanDate.getMonth();
      data[monthIndex] += parseFloat(convertedAmount);
    } catch (error) {
      console.error('Error parsing expense data:', error);
    }
  });

  return months.map((month, index) => ({
    name: month,
    amount: parseFloat(data[index]).toFixed(2)
  }));
};



  const chartData = formatDataForChart();

  // Calculate total expense for the last 12 months
  const totalExpense = chartData.reduce((total, data) => total + parseFloat(data.amount), 0).toFixed(2);

  // Get the current month
  const currentMonthIndex = new Date().getMonth();

  // Get the difference in expenses between the current month and the previous month
  const currentMonthExpense = parseFloat(chartData[currentMonthIndex].amount);
  const prevMonthExpense = parseFloat(chartData[(currentMonthIndex - 1 + 12) % 12].amount);
  const expenseDifference = currentMonthExpense - prevMonthExpense;

  // Calculate the percentage increase from the previous month
  const percentIncrease = ((expenseDifference / prevMonthExpense) * 100).toFixed(1);

  // Determine trend, state, and scale
  let trend, state, scale;
  if (expenseDifference > 0) {
    trend = "Up";
    state = "Error";
    scale = `+${expenseDifference.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  } else if (expenseDifference < 0) {
    trend = "Down";
    state = "Good";
    scale = `-${Math.abs(expenseDifference).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  } else {
    trend = "Up";
    state = "Good";
    scale = "No Change";
  }

  const formattedTotalExpense = parseFloat(totalExpense).toLocaleString();

  // Formatter function for y-axis labels
  const yAxisFormatter = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value;
  };

  return (
    <Card header={<AnalyticalCardHeader description="Last 12 Months" scale={scale} state={state} subtitleText="Expense" titleText="Total Cumulative Expenses" trend={trend} unitOfMeasurement="SAR" value={formattedTotalExpense}><NumericSideIndicator number={percentIncrease} titleText="Previous Month" unit="%"/></AnalyticalCardHeader>}>
      <LineChart
        dataset={chartData}
        dimensions={[{ accessor: 'name' }]}
        measures={[{ accessor: 'amount', label: 'Amount', formatter: yAxisFormatter }]}
        noLegend
      />
    </Card>
  );
};

export default ExpenseAmountDistributionCard;
